namespace Solovis.Reports.Parts.Tables.DataTableHelpers {

  /**
   * @param {Object} element - DOM element
   * @param {Object} htmlContent - HTML string, DOM element, or JQuery element.
   */
  var internalSetHtmlIfNonNull = function (element, htmlContent) {
    if (typeof (htmlContent) !== "undefined" && htmlContent !== null) {
      if (typeof htmlContent === "string") {
        element.insertAdjacentHTML("beforeend", htmlContent);
      }
      else if (htmlContent instanceof jQuery) {
        $(element).html(htmlContent as string);
      }
      else if (typeof htmlContent === "object") {
        element.appendChild(htmlContent);
      }
    }
  };

  /*
   * StandardTableRenderer is responsible for directly rendering out the table
   * without doing anything fancy such as virtual scrolling.
   * 
   * options:
   *   tableControl: Instance of the DataTableControl that this is being used for.
   *   tableContainer: The div which the table will be rendered into.
   */
  export class StandardTableRenderer {

    public static disallowDeferredRender: boolean = false;

    public tableControl: Solovis.Reports.Parts.Tables.DataTableControl;
    public _tableContainer;
    public _noFloatingHScroll: boolean;
    public _noDeferredRender: boolean;

    public _dataView;
    public _tableSettingsToUse;
    public _floatingHScrollEnabled: boolean;
    public _floatingColumnHeader: Solovis.Reports.Parts.Tables.DataTableHelpers.FloatingColumnHeader;
    public _floatingColumnHeaderDiv: JQuery;
    public _table: JQuery;
    public _lastTrList;
    public _initialSetup;
    public _scrollTimer;

    constructor(options) {
      var self = this;

      this.tableControl = options.tableControl;

      this._tableContainer = options.tableContainer;

      this._noFloatingHScroll = options.noFloatingHScroll ? true : false;

      this._noDeferredRender = Solovis.isNotNull(options.noDeferredRender) ? options.noDeferredRender : false;

      if (StandardTableRenderer.disallowDeferredRender) {
        // Check global setting that is used for testing purposes to disable deferred render.
        this._noDeferredRender = true;
      }

      this._initialSetup = true;

    }


    public renderDataView(dataView, tableSettingsToUse) {
      this._dataView = dataView;
      this._tableSettingsToUse = tableSettingsToUse;

      this._floatingHScrollEnabled = true;
      if (this._noFloatingHScroll) {
        this._floatingHScrollEnabled = false;
      }
      else {
        // See if floating hscroll is not permitted because we are inside of a dialog or custom panel.
        var hScrollNotAllowedCallback = function (element) {
          var jElement = $(element);
          if (jElement.hasClass("solovis-dialog")) {
            return true;
          }
          else if (jElement.hasClass("resizable-report-panel")) {
            return true;
          }

          return false;
        };

        if (Solovis.UI.DomHelper.hasMatchingParentOrSelf(this.tableControl.container, hScrollNotAllowedCallback)) {
          this._floatingHScrollEnabled = false;
        }
      }

      this._renderTableData();
    }

    public handleVisibleFixup() {
    }

    public _renderTableData() {
      var self = this;

      if (!this._tableContainer)
        return;

      this._tableContainer.html("");
      this._tableContainer.css("position", "relative");
      this._tableContainer.get(0).__solovisPostColumnResizeCallback = function () {
        // This should only be called in the event of a manual resize event.
        self._postTableColumnResizeCallback();
      };

      if (this._floatingHScrollEnabled) {
        this._tableContainer.css("overflow", "auto hidden");
        this._tableContainer.css("width", "100%");
      }
      else {
        this._tableContainer.css("overflow", "");
        this._tableContainer.css("width", "");
      }

      if (!this._floatingColumnHeader) {
        this._floatingColumnHeaderDiv = $("<div>").appendTo(this._tableContainer);

        if (this._tableSettingsToUse.marginCentered) {
          this._floatingColumnHeaderDiv.css("margin-left", "auto");
          this._floatingColumnHeaderDiv.css("margin-right", "auto");
          this._floatingColumnHeaderDiv.css("right", "0");
        }

        this._floatingColumnHeader = new Solovis.Reports.Parts.Tables.DataTableHelpers.FloatingColumnHeader({
          tableControl: this.tableControl,
          tableContainer: this._tableContainer,
          floatingColumnHeaderDiv: this._floatingColumnHeaderDiv,
          columnsGetter: function () {
            if (self._dataView) {
              return self._dataView.visibleColumns;
            }
            return null;
          },
          trueTableGetter: function () {
            return self._table;
          }
        });
      }

      if (!this._dataView)
        return;

      var columns = this._dataView.visibleColumns;
      var columnCount = columns ? columns.length : 0;

      // Determine aggregateDisplay and group header settings.
      var tableSettingsToUse = this.tableControl._tableSettingsToUse;
      var aggregateDisplay = tableSettingsToUse.aggregateRowDisplay;

      var groupHeaderColspan = columnCount;
      if (aggregateDisplay == "inline") {
        var desiredColspan = 1;
        if (tableSettingsToUse.groupHeaderColspan) {
          desiredColspan = tableSettingsToUse.groupHeaderColspan;
        }

        groupHeaderColspan = Math.min(desiredColspan, columnCount);
      }

      // The true-data-table is simply a css marker to help with locating this element in testing.
      var table = $("<table>");
      this._table = table;

      table.addClass("report-data-table true-data-table column-lines");

      table.appendTo(this._tableContainer);

      // NOTE: We used to have table-layout fixed as optional, but now it's required.
      table.css("table-layout", "fixed");

      if (this._tableSettingsToUse.marginCentered) {
        table.css("margin-left", "auto");
        table.css("margin-right", "auto");
      }

      if (this._tableSettingsToUse.freezeFirstColumn) {
        table.addClass("freeze-first-col");
      }

      StandardTableRenderer.appendHeaderRows(table, columns, this.tableControl);

      var tableBodyElement = document.createElement("tbody");
      var tableBody = $(tableBodyElement);

      var solovisTableRenderingData = {
        renderer: this,
        columns: columns,
        tableControl: this.tableControl,
        groupHeaderColspan: groupHeaderColspan,
        aggregateDisplay: aggregateDisplay
      };

      var trList = [];

      for (var i = 0; i < this._dataView.flattenedRows.length; i++) {
        var flattenedRow = this._dataView.flattenedRows[i];

        var tr: any = document.createElement("tr");
        tr.__solovisTableRendering = solovisTableRenderingData;
        tr.__solovisDelayRender = true;
        tr.__solovisFlattenedRow = flattenedRow;

        // Set a fixed height to placeholder that approximates single line height.
        tr.style.height = "28.56px";

        tableBodyElement.appendChild(tr);
        trList.push(tr);
      }

      this._lastTrList = trList;

      if (this._noDeferredRender) {
        // Immediately render all rows.
        this.ensureAllRowsRendered();
      } else if (this._initialSetup) {
        // Rows will only be rendered when they become visible due to scrolling or resizing.
        // This setup should only happen when first setting up the table.
        this._setupDeferredRender(trList, tableBodyElement);
        this._initialSetup = false;
      }

      tableBody.appendTo(table);

      var jTableContainer: any = $(this._tableContainer);
      if (this._floatingHScrollEnabled) {
        if (!jTableContainer.get(0).__solovisFloatingScrollbarApplied) {
          jTableContainer.get(0).__solovisFloatingScrollbarApplied = true;
          jTableContainer.solovisFloatingHScroll();
        }
      }
      else {
        if (jTableContainer.get(0).__solovisFloatingScrollbarApplied) {
          delete jTableContainer.get(0).__solovisFloatingScrollbarApplied;
          jTableContainer.solovisFloatingHScroll(false);
        }
      }

      if (this._floatingColumnHeader) {
        this._floatingColumnHeader.updateFloatingHeaderContent(true);
      }

      // We should ensure that all of the floating horizontal scrollbars are updated.
      if ((window as any).SolovisHorzScrollManagerCollection) {
        (window as any).SolovisHorzScrollManagerCollection.updateAllFloatingScrollbars();
      }
    }

    public ensureAllRowsRendered() {
      var trList = this._lastTrList;
      if (trList) {
        for (var rowCo = 0; rowCo < trList.length; rowCo++) {
          StandardTableRenderer.renderRow(trList[rowCo]);
        }
      }
    }


    public _setupDeferredRender(trList, tableBodyElement) {
      var self = this;

      if (trList) {
        var initialRowsToRender = Math.min(trList.length, 30);
        for (var rowCo = 0; rowCo < initialRowsToRender; rowCo++) {
          StandardTableRenderer.renderRow(trList[rowCo]);
        }

        // We will try to watch for scroll events on any container element that can scroll.
        // TODO: In theory, we should probably also come up with a solution for resizing dialog elements.
        var scrollables = Solovis.UI.DomHelper.getVerticalScrollableContainers(this.tableControl.controlHost.container);

        var renderVisibleRowsCallback = function () {
          var visibleRows = self._getVisibleRowRange(trList, tableBodyElement);
          if (visibleRows.tableBodyRemoved) {
            $(scrollables).each(function (i, scrollable) {
              scrollable.removeEventListener("scroll", renderVisibleRowsCallback);
            });

            window.removeEventListener("resize", renderVisibleRowsCallback);
          }

          if (visibleRows && typeof visibleRows.minRow === "number" && typeof visibleRows.maxRow === "number") {
            for (var rowCo = visibleRows.minRow; rowCo <= visibleRows.maxRow; rowCo++) {
              StandardTableRenderer.renderRow(trList[rowCo]);
            }
          }
        };

        $(scrollables).each((i, scrollable) => {
          scrollable.addEventListener("scroll", renderVisibleRowsCallback);
        });
        window.addEventListener("resize", renderVisibleRowsCallback);

        // Do one check for visible rows immediately.
        setTimeout(renderVisibleRowsCallback, 100);
      }
    };

    /**
     * @param {Object[]} trList - given array of tr elements in order, determine which are visible in the viewport.
     * @param {Object} tableBodyElement - DOM element for the tbody element that rows belong to.
     */
    public _getVisibleRowRange(trList, tableBodyElement): any {
      if (!Solovis.UI.DomHelper.isDescendantOf(tableBodyElement, document.body)) {
        return { tableBodyRemoved: true };
      }

      function isElementInViewport(el): boolean {
        return compareViewport(el) === 0;
      }

      function compareViewport(el): number {
        var rect = el.getBoundingClientRect();
        if (rect.top < 0) {
          return -1;
        }
        if (rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)) {
          return 0;
        }

        return 1;
      }

      var minRow = null;
      var maxRow = null;
      var index = InvData.BinarySearchHelper.binarySearch(trList, (el) => compareViewport(el));

      // If we failed to binary search, we will just iterate through all elements.
      if (index === -1) {
        $(trList).each(function (i, tr) {
          if (isElementInViewport(tr)) {
            if (minRow === null) {
              minRow = i;
            }

            maxRow = i;
          }
        });
      }
      else {
        minRow = index;
        maxRow = index;
        while (minRow > 0 && trList[minRow] && isElementInViewport(trList[minRow])) {
          minRow--;
        }
        while (maxRow < (trList.length - 1) && trList[maxRow] && isElementInViewport(trList[maxRow])) {
          maxRow++;
        }
      }

      // Determine first row that was already visible. We may want want to remember scroll position
      // for this one so that we don't shift view.
      var firstAlreadyVisibleRow = null;
      if (typeof minRow === "number") {
        for (var rowCo = minRow; rowCo <= maxRow; rowCo++) {
          var tr = trList[rowCo];
          if (!tr.__solovisDelayRender) {
            firstAlreadyVisibleRow = rowCo;
            break;
          }
        }
      }

      var result = {
        minRow: minRow,
        maxRow: maxRow,
        firstAlreadyVisibleRow: firstAlreadyVisibleRow
      };

      return result;
    }

    public _postTableColumnResizeCallback() {
      if (this._floatingColumnHeader) {
        this._floatingColumnHeader.updateFloatingHeaderContent();
      }

      // We should ensure that all of the floating horizontal scrollbars are updated.
      if ((window as any).SolovisHorzScrollManagerCollection) {
        (window as any).SolovisHorzScrollManagerCollection.updateAllFloatingScrollbars();
      }
    }


    /**
     * @param {Object} tableElement - DOM element for table.
     * @param {Object[]} columns - array of column definitions to use.
     * @param {boolean} fitToScreen - width of table is 100% if true.
     */
    public static appendColgroup(tableElement, columns, fitToScreen) {
      if (columns) {
        var totalWidth = 0;

        // Add colgroup for columns.
        var colgroup = document.createElement("colgroup");
        $(columns).each(function (i, col: any) {
          var colgroupCol = document.createElement("col");
          colgroup.appendChild(colgroupCol);

          var colWidth;
          if (typeof col.width == "number") {
            colWidth = col.width;
          }
          else {
            // Assign arbitrary width.
            colWidth = 50;
          }

          $(colgroupCol).width(colWidth);
          totalWidth += parseInt(colWidth);
        });

        tableElement.appendChild(colgroup);

        if (fitToScreen) {
          $(tableElement).width("100%");
        }
        else {
          $(tableElement).width(totalWidth);
        }
      }
    };


    /**
     * @param {Object} table - DOM element for table
     * @param {Object[]} columns - array of column definitions to use
     * @param {Object} tableControl - instance of Solovis.Reports.Parts.Tables.DataTableControl
     */
    public static appendHeaderRows(table, columns, tableControl: Solovis.Reports.Parts.Tables.DataTableControl) {
      if (columns) {
        var tableSettingsToUse = tableControl._tableSettingsToUse;
        var fitToScreen = tableSettingsToUse.fitToScreen;

        StandardTableRenderer.appendColgroup($(table).get(0), columns, fitToScreen);

        // Check for super headers.
        var maxSuperHeaders = 0;
        $(columns).each(function (i, col: any) {
          if (col.superHeaders) {
            maxSuperHeaders = Math.max(maxSuperHeaders, col.superHeaders.length);
          }
        });

        var tableHeaderSection = $("<thead>").appendTo(table);

        for (var superHeaderIndex = maxSuperHeaders - 1; superHeaderIndex >= 0; superHeaderIndex--) {
          var superHeaderRow = $("<tr>").appendTo(tableHeaderSection);
          superHeaderRow.addClass("super-header-row");
          superHeaderRow.addClass("super-header-row-" + superHeaderIndex);

          var superHeaderLabels = [];
          $(columns).each(function (i, col: any) {
            if (col.superHeaders && col.superHeaders.length > superHeaderIndex) {
              superHeaderLabels.push(col.superHeaders[superHeaderIndex]);
            }
            else {
              superHeaderLabels.push(null);
            }
          });

          $(columns).each(function (i, col: any) {
            const superHeaderLabel = superHeaderLabels[i];

            // While frozen, column 0 always gets its own single-column header cell 
            // so the sticky first cell in every header row looks correct.
            if (i === 0 || (tableSettingsToUse.freezeFirstColumn && i === 1) || (superHeaderLabels[i - 1] !== superHeaderLabel)) {
              const superHeaderCell = $("<th>").appendTo(superHeaderRow);

              // Determine colspan by consecutive headers with same label.
              let colspan = 1;
              if (i > 0 || !tableSettingsToUse.freezeFirstColumn) {
                while (i + colspan < superHeaderLabels.length) {
                  if (superHeaderLabels[i + colspan] !== superHeaderLabel) {
                    break;
                  }

                  colspan++;
                }
              }

              if (colspan > 1) {
                superHeaderCell.attr("colspan", colspan.toString());
              }

              if (superHeaderLabel !== null) {
                superHeaderCell.text(superHeaderLabels[i]);
                superHeaderCell.addClass("super-header-label");

                superHeaderCell.attr("title", superHeaderLabels[i]);
                superHeaderCell.css("text-align", "center");
              }
            }
          });
        }

        var tableHeaderRow = $("<tr>").appendTo(tableHeaderSection);
        $(columns).each(function (i, col: any) {
          const colObj = tableControl.columnSet.getColumnById(col.id);

          const headerCell = $("<th>").addClass("header-label").appendTo(tableHeaderRow).css({
            "padding-left": ".3rem",
            "padding-right": ".3rem"
          });
          const headerCellElement: any = headerCell.get(0);
          headerCellElement.__dataTableColumnId = col.id;

          const headerDiv = $("<div>").addClass("clamp-label").appendTo(headerCell);

          if (col.labelHtml) {
            headerDiv.html(col.labelHtml);
          }
          else if (col.label) {
            headerDiv.text(col.label);
          }

          headerCell.attr("title", colObj?.getCompositeLabel());

          headerCell.css("text-align", col.headerAlign || "center");

          var currentSortOrder = tableControl.sortManager.getCurrentSortOrderForId(col.id);
          if (currentSortOrder) {
            var handleSortToggle = function (colId, e) {
              e.stopPropagation();
              if (!e.ctrlKey) {
                tableControl.sortManager.toggleSortDirection(col.id, "ColumnHeaderClick");
              }
              else {
                // Turn off sorting on this column when ctrl-clicked.
                tableControl.sortManager.removeSortById(col.id, "ColumnHeaderClick");
              }
            };

            var upCaret = $("<span>");
            var downCaret = $("<span>");

            upCaret.addClass("fa fa-caret-up")
            downCaret.addClass("fa fa-caret-down")

            if (currentSortOrder == "asc") {
              headerDiv.append(" ");
              upCaret.appendTo(headerDiv);
              upCaret.click(function (e) {
                handleSortToggle(col.id, e);
              });
            }
            else if (currentSortOrder == "desc") {
              headerDiv.append(" ");
              downCaret.appendTo(headerDiv);
              downCaret.click(function (e) {
                handleSortToggle(col.id, e);
              });
            }
          }
          else {
            var sortForCol = tableControl.sortManager.getSortById(col.id);
            if (sortForCol) {
              headerDiv.append(" ");
              var sortCarets = $("<span>");
              sortCarets.addClass("fa fa-sort no-print");
              sortCarets.appendTo(headerDiv);
              sortCarets.click(function (e) {
                e.stopPropagation();
                var newSortByEntry = { id: col.id, desc: !!sortForCol.desc };
                var newSortByList;
                if (!e.ctrlKey) {
                  // Add this column to the list of sticky sorts.
                  newSortByList = tableControl.sortManager.getStickySortByList();
                  newSortByList.push(newSortByEntry);
                }
                else {
                  // Add this column to the list of sorts.
                  newSortByList = tableControl.sortManager.getSortByList();
                  newSortByList.push(newSortByEntry);
                }

                tableControl.sortManager.updateSortByList(newSortByList, "ColumnHeaderClick");
              });
            }
          }

          Solovis.UI.PopupLinkPanel.onElementClick(headerCell, function (clickEvent) {
            tableControl._showColumnHeaderMenu(col.id, clickEvent);
          });

          DataTableHelpers.VisualColumnResizer.addResizeHandler(headerCell, tableControl);
        });
      }
    };


    /**
     * @param {Object} tr - tr DOM element for row where tr.__solovisFlattenedRow is set.
     */
    public static renderRow(tr) {
      var rowData = tr.__solovisFlattenedRow;
      if (!rowData) {
        return;
      }

      var delayRender = tr.__solovisDelayRender;
      if (!delayRender) {
        return;
      }

      // Remove __solovisDelayRender so we know we have already rendered this row.
      delete tr.__solovisDelayRender;

      // Remove fixed height so content will control the scaling.
      tr.style.cssText = null;

      var solovisTableRendering = tr.__solovisTableRendering;
      var tableRenderer = solovisTableRendering.renderer;

      var columns = solovisTableRendering.columns;
      var tableControl = solovisTableRendering.tableControl;

      var row = rowData.row;
      var level = rowData.level;

      if (rowData.isGroupHeader) {
        var groupHeaderColspan = solovisTableRendering.groupHeaderColspan;

        tableRenderer._renderGroupHeaderRow(tr, rowData, row, columns, level, groupHeaderColspan, rowData.inlineAggregates);
      }
      else if (rowData.isAggregateRow) {
        var aggregateDisplay = solovisTableRendering.aggregateDisplay;

        tableRenderer._renderAggregateRow(tr, row, columns, level, aggregateDisplay);
      }
      else {
        // Regular row.                    
        var tableRowElement = tr;

        tableRowElement.__solovisLevel = level;

        // This is a regular row.
        if (columns) {
          $(columns).each(function (colIndex, col) {
            var cellContext = {
              reportControl: tableControl.reportControl,
              dataTable: tableControl,
              row: row,
              level: level,
              modelEnvironment: tableControl.modelEnvironment,
              col: col,
              colIndex: colIndex,
              defaultFormatter: Solovis.Reports.Formatter.formatValueAsHtml
            };

            var cellValue = tableControl._getColumnValueForRow(col, row);

            var cellElement = document.createElement("td");

            if (Column.isColEditableForRow(col, row)) {
              StandardTableRenderer._attachCellEditor(cellElement, cellValue, col, row, tableControl);
            }
            else {
              const cellValueHtml = tableControl._formatCellHtml(cellValue, cellContext);
              internalSetHtmlIfNonNull(cellElement, cellValueHtml);
            }

            var tableCellStyle = tableControl._getCellStyle(cellValue, cellContext);
            if (tableCellStyle) {
              cellElement.style.cssText = tableCellStyle;
            }

            var pxIncreaseTextIndent = 15;
            // When row is grouped the first column should be indented.
            if (colIndex == 0 && level > 0) {
              cellElement.style.textIndent = (level * pxIncreaseTextIndent) + "px";
            }

            tableControl._applyColumnCellStyles(cellElement, col);

            tableRenderer._addCellContextMenuIfConfigured(cellElement, cellContext);

            tableRowElement.appendChild(cellElement);
          });
        }

        if (tableControl._rowStyleUpdater) {
          tableControl._rowStyleUpdater(tableRowElement, row);
        }
      }
    }


    /**
     * Builds the editor for an editable cell and attaches it to the td. Falls back to the
     * normal formatted-HTML path when no editor factory is registered for the column's data
     * type — that's an unsupported-data-type signal that we want to log rather than crash.
     */
    private static _attachCellEditor(cellElement: HTMLElement, cellValue: any, col: any, row: any, tableControl: DataTableControl): void {
      const editorType = CellEditors.resolveEditorTypeForColumn(col);
      const factory = editorType ? CellEditors.getEditor(editorType) : null;
      if (!factory) {
        console.warn("DataTableControl: no cell editor registered for column", { columnId: col?.id, dataType: col?.dataType, requestedType: editorType });
        const cellValueHtml = tableControl._formatCellHtml(cellValue, { col: col, row: row, dataTable: tableControl });
        internalSetHtmlIfNonNull(cellElement, cellValueHtml);
        return;
      }

      const editor = factory(cellValue, { row: row, col: col, tableControl: tableControl }, (newValue) => {
        tableControl.onRowCellChanged(row, col, cellValue, newValue);
      });

      // The input fills the cell edge-to-edge — drop the td padding so the input border lines
      // up with the column boundary (table-layout: fixed already pins the column width).
      cellElement.style.padding = "0";

      const existingWarning = cellElement.querySelector(".fa.fa-exclamation-triangle");
      if (existingWarning) {
        existingWarning.parentNode?.removeChild(existingWarning);
      }

      const warningMessage = row?.data?.[Solovis.Reports.ReportHelpers.ProForma.CELL_WARNING_KEY_PREFIX + col?.id];

      const cellContainer = document.createElement("div");
      cellContainer.style.display = "flex";
      cellContainer.style.alignItems = "center";
      cellContainer.style.width = "100%";

      editor.element.style.width = "";
      editor.element.style.minWidth = "0";
      editor.element.style.flex = "1";
      cellContainer.appendChild(editor.element);

      if (warningMessage) {
        const warnSpan = document.createElement("span");
        warnSpan.className = "fa fa-exclamation-triangle";
        warnSpan.style.margin = "0 4px";
        $(warnSpan).solovisTooltip({ text: warningMessage });
        cellContainer.appendChild(warnSpan);
      }
      cellElement.appendChild(cellContainer);
    }


    public _addCellContextMenuIfConfigured(cellElement, cellContext) {
      cellElement.addEventListener("contextmenu", (e) => {
        let links = [];
        if (this._tableSettingsToUse && this._tableSettingsToUse.cellContextMenuItems) {
          let cellLinks = this._tableSettingsToUse.cellContextMenuItems(cellContext);
          if (cellLinks) {
            links = links.concat(cellLinks);
          }
        }

        let tableLinks = cellContext.dataTable._getTableContextMenuItems({
          cellContext: cellContext
        });
        if (!_.isEmpty(tableLinks)) {
          if (!_.isEmpty(links)) {
            links.push({ separator: true });
          }

          links = links.concat(tableLinks);
        }

        if (!_.isEmpty(links)) {
          e.preventDefault();
          e.cancelBubble = true;

          Solovis.UI.PopupLinkPanel.showLinks({
            links: links,
            offset: { left: e.pageX, top: e.pageY },
            useDefaultPositionBoundary: true,
            useDefaultPositionOf: true,
          });

          return false;
        }
      });
    }


    public _renderGroupHeaderRow(tr, rowData, row, columns, level, groupHeaderColspan, displayAggregatesInline) {
      var self = this;
      var tableControl = this.tableControl;

      if (row._subRowReplacement) {
        // We always use group header colspan of 1 and displayAggregates inline on sub-row replacements.
        groupHeaderColspan = 1;
        displayAggregatesInline = true;
      }

      var tableRowElement = tr;
      var tableRow = $(tableRowElement);

      tableRowElement.className = "group-header-row group-header-row-" + level;
      tableRowElement.__solovisGroupHeaderRow = true;
      tableRowElement.__solovisLevel = level;

      var groupHeaderCell = document.createElement("td");
      groupHeaderCell.className = "group-header-cell group-header-cell-" + level;
      tableRowElement.appendChild(groupHeaderCell);

      if (this._tableSettingsToUse.freezeFirstColumn && groupHeaderColspan > 1) {
        // A single colspan'd cell can't be part sticky and part scrolling, so split it into a
        // sticky first column plus a plain filler covering the rest.
        groupHeaderCell.colSpan = 1;

        const groupHeaderFillerCell = document.createElement("td");
        groupHeaderFillerCell.colSpan = groupHeaderColspan - 1;
        groupHeaderFillerCell.className = "group-header-filler-cell";
        tableRowElement.appendChild(groupHeaderFillerCell);
      }
      else {
        groupHeaderCell.colSpan = groupHeaderColspan;
      }

      // This is a group header row.
      var group = tableControl.groupManager.getGroupById(row._groupId);
      if (!group) {
        if (row._subRowReplacement) {
          var col = columns[0];
          // We'll use the normal way of rendering first row for sub-row replacement rows.
          var cellValue = self.tableControl._getColumnValueForRow(col, row._backingRow);

          let cellContext = {
            reportControl: tableControl.reportControl,
            dataTable: tableControl,
            row: row._backingRow,
            level: level,
            modelEnvironment: tableControl.modelEnvironment,
            col: col,
            defaultFormatter: Solovis.Reports.Formatter.formatValueAsHtml
          };

          var cellValueHtml = tableControl._formatCellHtml(cellValue, cellContext);
          internalSetHtmlIfNonNull(groupHeaderCell, cellValueHtml);

          this._addCellContextMenuIfConfigured(groupHeaderCell, cellContext);
        }
        else {
          groupHeaderCell.textContent = "Group: " + row._groupId;
        }
      }
      else {
        let cellContext = {
          reportControl: tableControl.reportControl,
          dataTable: tableControl,
          row: row,
          level: level,
          modelEnvironment: tableControl.modelEnvironment,
          group: group,
          defaultFormatter: Solovis.Reports.Formatter.formatValueAsHtml
        };

        var groupRep = row._groupRep;
        var groupHeaderHtml = tableControl._formatCellHtml(groupRep, cellContext);
        if (groupHeaderHtml === null || groupHeaderHtml === "")
          groupHeaderHtml = "[Unknown]";

        internalSetHtmlIfNonNull(groupHeaderCell, groupHeaderHtml);

        this._addCellContextMenuIfConfigured(groupHeaderCell, cellContext);

        tableRowElement.__solovisGroupRep = groupRep;
      }

      tableRowElement.__solovisNestedGroupKey = row._nestedGroupKey;

      var hasChildRows = !_.isEmpty(row._rows);

      var expandArrowSpan = $("<span>");
      var collapseArrowSpan = $("<span>");
      expandArrowSpan.addClass("fa fa-caret-right")
        .css("font-size", "19px")
        .css("position", "absolute")
        .css("top", "-1px");
      collapseArrowSpan.addClass("fa fa-caret-down")
        .css("font-size", "19px")
        .css("position", "absolute")
        .css("top", "-1px");

      if (hasChildRows) {
        // Only show expand/collapse links if there are child rows.
        if (rowData.expanded) {
          var collapseLink = $("<a>");
          collapseLink.addClass("no-print");
          collapseLink.css("margin-left", "10px");
          collapseLink.css("display", "inline-block");
          collapseLink.css("position", "relative");
          collapseLink.css("vertical-align", "top");
          collapseLink.css("height", "15px");

          collapseArrowSpan.appendTo(collapseLink);

          collapseLink.appendTo(groupHeaderCell);

          collapseLink.click(function () {
            tableControl.expansionManager.collapse(rowData.nestedGroupKey);
          });
        }
        else {
          var expandLink = $("<a>");
          expandLink.addClass("no-print");
          expandLink.css("margin-left", "10px");
          expandLink.css("display", "inline-block");
          expandLink.css("position", "relative");
          expandLink.css("vertical-align", "top");
          expandLink.css("height", "15px");

          expandArrowSpan.appendTo(expandLink);

          expandLink.appendTo(groupHeaderCell);

          expandLink.click(function () {
            tableControl.expansionManager.expand(rowData.nestedGroupKey);
          });
        }
      }

      let cellValueGetter = function (row, col) {
        if (row._backingRow) {
          // If column has explicit aggregation, we should use that over locally aggregated value.
          let colObject = tableControl.columnSet.getColumnById(col.id);
          if (colObject && colObject.mods && colObject.mods.aggregation) {
            let cellValue = row._groupTotals ? row._groupTotals[col.id] : null;
            return cellValue;
          }
          else {
            let cellValue = self.tableControl._getColumnValueForRow(col, row._backingRow);
            return cellValue;
          }
        }
        else {
          let cellValue = row._groupTotals ? row._groupTotals[col.id] : null;
          return cellValue;
        }
      };


      if (displayAggregatesInline) {
        if (columns) {
          $(columns).each(function (i, col: any) {
            // We will only see aggregates not covered by group header label.
            if (i >= groupHeaderColspan) {
              var id = col.id;
              var cellValue = cellValueGetter(row, col);

              let cellContext = {
                reportControl: tableControl.reportControl,
                dataTable: tableControl,
                row: row,
                level: level,
                modelEnvironment: tableControl.modelEnvironment,
                col: col,
                defaultFormatter: Solovis.Reports.Formatter.formatValueAsHtml,
                aggregate: true
              };

              var cellValueHtml = tableControl._formatCellHtml(cellValue, cellContext);

              var cell = document.createElement("td");

              internalSetHtmlIfNonNull(cell, cellValueHtml);

              var tableCellStyle = tableControl._getCellStyle(cellValue, cellContext);
              if (tableCellStyle) {
                cell.style.cssText = tableCellStyle;
              }

              tableControl._applyColumnCellStyles(cell, col);

              self._addCellContextMenuIfConfigured(cell, cellContext);

              tableRowElement.appendChild(cell);
            }
          });
        }
      }
    }

    public _renderAggregateRow(tr, row, columns, level, displayPosition) {
      var self = this;
      var tableControl = this.tableControl;

      if (row._groupTotals || row._backingRow) {
        var cellValueGetter;
        if (row._backingRow) {
          cellValueGetter = function (row, col) {
            var cellValue = self.tableControl._getColumnValueForRow(col, row._backingRow);
            return cellValue;
          };
        }
        else {
          cellValueGetter = function (row, col) {
            var cellValue = row._groupTotals ? row._groupTotals[col.id] : null;
            return cellValue;
          };
        }

        var tableRowElement = tr;

        tableRowElement.className = "aggregate-row aggregate-row-" + displayPosition;
        tableRowElement.__solovisAggregateRow = true;
        tableRowElement.__solovisLevel = level;

        if (columns) {
          $(columns).each(function (i, col) {
            var id = col.id;
            var cellValue = cellValueGetter(row, col);

            var cellContext = {
              reportControl: tableControl.reportControl,
              dataTable: tableControl,
              row: row,
              level: level,
              modelEnvironment: tableControl.modelEnvironment,
              col: col,
              defaultFormatter: Solovis.Reports.Formatter.formatValueAsHtml,
              aggregate: true
            };

            var cellValueHtml = tableControl._formatCellHtml(cellValue, cellContext);

            var cell = document.createElement("td");
            internalSetHtmlIfNonNull(cell, cellValueHtml);

            var tableCellStyle = tableControl._getCellStyle(cellValue, cellContext);
            if (tableCellStyle) {
              cell.style.cssText = tableCellStyle;
            }

            tableControl._applyColumnCellStyles(cell, col);

            self._addCellContextMenuIfConfigured(cell, cellContext);

            tableRowElement.appendChild(cell);
          });
        }
      }
    }
  }

}

